import type { ReactNode } from 'react';
import { AtSign, Building2, CalendarDays, ShieldCheck, Target, Activity, CircleDot } from 'lucide-react';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Avatar } from '@/shared/components/ui/Avatar';
import { Badge } from '@/shared/components/ui/Badge';
import { Select } from '@/shared/components/ui/Select';
import { DEPT_LABELS, authorityLabel, authorityTone, fmtDate } from '../constants';
import type { HrUser } from '../types';

interface EmployeeDrawerProps {
  member: HrUser | null;
  open: boolean;
  /** Review/management actions (reset password, enable/disable) when authorized. */
  actions?: ReactNode;
  /** Superadmin-only: allow promoting/demoting between Administrator and Employee. */
  canManageRole?: boolean;
  roleChanging?: boolean;
  onChangeRole?: (id: string, role: 'admin' | 'employee') => void;
  onClose: () => void;
}

/**
 * Read-only employee detail. Shows ONLY fields the backend genuinely returns
 * from GET /api/admin/users: identity, authority tier (derived), department,
 * username, account status, live check-in, created date, and the two real
 * activity signals (lead_count, last_job_at). No email/phone/manager/designation
 * exist in the backend, so those sections are intentionally absent.
 */
export function EmployeeDrawer({ member, open, actions, canManageRole, roleChanging, onChangeRole, onClose }: EmployeeDrawerProps) {
  if (!member) return null;

  const currentRole: 'admin' | 'employee' = member.role === 'admin' ? 'admin' : 'employee';

  return (
    <Drawer open={open} onClose={onClose} title="Employee" width="w-[440px]">
      <div className="flex flex-col gap-5">
        {/* Identity */}
        <div className="flex items-center gap-3">
          <Avatar name={member.display_name} size="lg" />
          <div className="min-w-0">
            <div className="text-data-lg font-semibold text-on-surface">{member.display_name}</div>
            <div className="flex items-center gap-1 text-body-sm text-on-surface-variant">
              <AtSign className="h-3.5 w-3.5" /> {member.username}
            </div>
          </div>
        </div>

        {/* Authority + status */}
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={authorityTone(member.role)}>
            <ShieldCheck className="h-3 w-3" /> {authorityLabel(member.role)}
          </Badge>
          <Badge tone={member.active ? 'success' : 'danger'}>{member.active ? 'Active' : 'Disabled'}</Badge>
          {member.is_checked_in && (
            <Badge tone="info">
              <CircleDot className="h-3 w-3" /> Checked In
            </Badge>
          )}
        </div>

        {/* Organization */}
        <Section title="Organization">
          <Row icon={Building2} label="Department" value={DEPT_LABELS[member.department ?? ''] ?? member.department ?? '—'} />
          <Row icon={ShieldCheck} label="Authority Tier" value={authorityLabel(member.role)} />
          <Row icon={CalendarDays} label="Joined" value={fmtDate(member.created_at)} />
        </Section>

        {/* Activity (real signals only) */}
        <Section title="Activity">
          <Row icon={Target} label="Leads Owned" value={String(member.lead_count ?? 0)} />
          <Row
            icon={Activity}
            label="Last Scrape Job"
            value={member.last_job_at ? fmtDate(member.last_job_at) : 'No jobs run'}
          />
        </Section>

        {/* Authority management (superadmin only) */}
        {canManageRole && (
          <Section title="Authority">
            <Select
              name="member-role"
              value={currentRole}
              disabled={roleChanging}
              onChange={(e) => onChangeRole?.(member.id, e.target.value as 'admin' | 'employee')}
              options={[
                { value: 'employee', label: 'Employee' },
                { value: 'admin', label: 'Administrator' },
              ]}
            />
            <p className="text-body-sm text-on-surface-variant">
              Changing the role updates this user's authority across the platform.
            </p>
          </Section>
        )}

        {actions && <div className="flex flex-wrap items-center gap-2 border-t border-outline pt-4">{actions}</div>}
      </div>
    </Drawer>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-label-caps font-semibold uppercase text-on-surface-variant">{title}</span>
      <div className="flex flex-col gap-2 rounded-card-sm border border-outline bg-surface-container-low px-3 py-2.5">
        {children}
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }: { icon: typeof Building2; label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 text-data-md">
      <span className="flex items-center gap-2 text-on-surface-variant">
        <Icon className="h-4 w-4" /> {label}
      </span>
      <span className="font-medium text-on-surface">{value}</span>
    </div>
  );
}
